import LocationField from "kinnect-front/Locations/LocationField";
import VendorField from "kinnect-front/FacilityMaintenance/Vendors/VendorField";
import {
	FacilityMaintenanceContact,
	FacilityMaintenanceVendor,
	Location
} from "kinnect-models";
import { FetchesData } from "kinnect-mixins";
import {
	KCurrencyField,
	KForm,
	KPhoneLink,
	KTextField,
	KTextarea
} from "kinnect-general";
import {
	VCard,
	VCol,
	VExpandTransition,
	VIcon,
	VList,
	VListItem,
	VListItemAction,
	VListItemContent,
	VListItemTitle,
	VRow,
	VSubheader,
	VDivider
} from "vuetify/lib";

export default KForm.extend( {
	name: "FacilityMaintenanceWorkOrderForm",

	props: {
		redirect: {
			...KForm.options.props.redirect,
			default: () => [ "back", { name: "facilityMaintenanceWorkOrders" } ]
		},

		workOrder: Object
	},

	mixins: [ FetchesData ],

	data: () => ( {
		contacts: [],
		formFields: {
			cost: null,
			idLocation: null,
			idVendor: null,
			leadTimeMinutes: null,
			materialCost: null,
			materialCostNotes: null,
			notes: null
		},
		locations: [],
		vendors: []
	} ),

	computed: {
		editing() {
			return Boolean( this.workOrder );
		},

		formUrl() {
			return this.editing
				? `facility-maintenance/work-orders/${this.workOrder.id}`
				: "facility-maintenance/work-orders";
		},

		vendorsForLocation() {
			if ( !this.formFields.idLocation ) {
				return;
			}

			return this.vendors.filter( vendor =>
				this.contacts.find( contact =>
					contact.idVendor === vendor.id
					&& contact.locations.find( location =>
						location.id === this.formFields.idLocation
					)
				)
			);
		},

		visibleContacts() {
			return this.contacts.filter( contact =>
				contact.idVendor === this.formFields.idVendor
				&& contact.locations.find( location => location.id === this.formFields.idLocation )
			);
		},

		genContacts() {
			if ( !this.visibleContacts.length ) {
				return;
			}

			return [
				<VDivider class="mt-3"></VDivider>,
				<VSubheader>Contacts</VSubheader>,
				<VRow>{this.visibleContacts.map( this.genContact )}</VRow>
			];
		}
	},

	watch: {
		workOrder: {
			handler( workOrder ) {
				if ( workOrder ) {
					Object.keys( this.formFields ).forEach( key => {
						if ( workOrder.hasOwnProperty( key ) ) {
							this.formFields[key] = workOrder[key];
						}
					} );
				}
			},
			immediate: true
		}
	},

	methods: {
		genContact( contact ) {
			return <VCol
				cols={12}
				sm={6}
			>
				<VCard height="100%" outlined>
					<VList class="list--auto-height" dense>
						<VListItem>
							<VListItemAction>
								<VIcon>person</VIcon>
							</VListItemAction>

							<VListItemContent>
								<VListItemTitle>
									{contact.name}
								</VListItemTitle>
							</VListItemContent>
						</VListItem>

						{contact.email && <VListItem>
							<VListItemAction>
								<VIcon>email</VIcon>
							</VListItemAction>

							<VListItemContent>
								<VListItemTitle>
									{contact.email}
								</VListItemTitle>
							</VListItemContent>
						</VListItem>}

						{contact.phone && <VListItem>
							<VListItemAction>
								<VIcon>phone</VIcon>
							</VListItemAction>

							<VListItemContent>
								<VListItemTitle>
									<KPhoneLink
										icon={null}
										phone={contact.phone}
									></KPhoneLink>
								</VListItemTitle>
							</VListItemContent>
						</VListItem>}

						{contact.isPreferredContactMethod && <VListItem>
							<VListItemAction>
								<VIcon>done</VIcon>
							</VListItemAction>

							<VListItemContent>
								<VListItemTitle>
									Preferred contact method
								</VListItemTitle>
							</VListItemContent>
						</VListItem>}

						{contact.notes && <VListItem>
							<VListItemAction>
								<VIcon>comment</VIcon>
							</VListItemAction>

							<VListItemContent>
								<VListItemTitle>
									{contact.notes}
								</VListItemTitle>
							</VListItemContent>
						</VListItem>}
					</VList>
				</VCard>
			</VCol>;
		},

		onCreateVendor( vendor ) {
			this.vendors.push( vendor );
			this.formFields.idVendor = vendor.id;
		},

		onCreateLocation( location ) {
			this.locations.push( location );
			this.formFields.idLocation = location.id;
		},

		async fetchVendors() {
			this.vendors = await FacilityMaintenanceVendor.get();
		},

		async fetchLocations() {
			this.locations = await Location.get();
		},

		async fetchContacts() {
			this.contacts = await FacilityMaintenanceContact.get();
		},

		async fetch() {
			await this.$http.all( [
				this.fetchVendors(),
				this.fetchLocations(),
				this.fetchContacts()
			] );
		}
	},

	render() {
		return <KForm
			attrs={{
				...this.$props,
				action: this.formUrl,
				data: this.formFields,
				method: this.editing ? "put" : "post"
			}}
			on={this.$listeners}
		>
			<LocationField
				items={this.locations}
				label="Location*"
				loading={this.$wait.is( "get locations" )}
				onCreate={this.onCreateLocation}
				required
				showAddButton={this.$can( "add_locations" )}
				vModel={this.formFields.idLocation}
				validationId="locations"
				validationName="location(s)"
				validationRules="required"
			></LocationField>

			<VExpandTransition>
				{this.formFields.idLocation && <VendorField
					deletableChips
					items={this.vendorsForLocation}
					label="Vendor"
					loading={this.$wait.is( "get vendors" )}
					onCreate={this.onCreateVendor}
					showAddButton={this.$can( "add_facility_maintenance_vendors" )}
					vModel={this.formFields.idVendor}
					validationId="idVendor"
					validationName="vendor"
				></VendorField>}
			</VExpandTransition>

			<KCurrencyField
				label="Cost"
				vModel={this.formFields.cost}
				validationName="cost"
				validationRules="min_value:0"
			></KCurrencyField>

			<KCurrencyField
				label="Material Cost"
				vModel={this.formFields.materialCost}
				validationId="materialCost"
				validationName="material cost"
				validationRules="min_value:0"
			></KCurrencyField>

			<VExpandTransition>
				{this.formFields.materialCost && <KTextarea
					label="Material Cost Notes"
					prependIcon="comment"
					vModel={this.formFields.materialCostNotes}
					validationId="materialCostNotes"
					validationName="material cost notes"
				></KTextarea>}
			</VExpandTransition>

			<KTextField
				label="Lead Time"
				min="0"
				onInput={val => this.formFields.leadTimeMinutes = parseFloat( val || 0 )}
				prependIcon="timer"
				step="5"
				suffix="minutes"
				type="number"
				validationId="leadTimeMinutes"
				validationName="lead time"
				value={this.formFields.leadTimeMinutes}
			></KTextField>

			<KTextarea
				label="Notes"
				prependIcon="comment"
				vModel={this.formFields.notes}
				validationName="notes"
			></KTextarea>

			{this.genContacts}
		</KForm>;
	}
} );
